import { useState, useEffect } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowRightFromBracket,
  faBook,
  faCalculator,
  faCircleInfo,
  faEdit,
  faMoneyBillWave,
  faPen,
  faRoute,
  faSave,
  faSpinner,
  faTag,
  faCheck,
  faTrash,
  faXmark,
} from '@/fontawesome';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import EmptyState from '@/components/ui/EmptyState';
import ErrorState from '@/components/ui/ErrorState';
import MarkdownEditor from '@/components/ui/MarkdownEditor';
import MarkdownRenderer from '@/components/ui/MarkdownRenderer';
import Modal from '@/components/ui/Modal';
import OptionTradeModal from '@/components/journal/OptionTradeModal';
import { APIRoutes } from '@/services/api.routes';
import { APIRequestError } from '@/services/api';
import { JOURNAL_ROUTES } from '@/constants/routes';
import {
  dateToRFCDateValue,
  formatCurrency,
  formatCurrencyCentValue,
  formatRFCDate,
  toCurrencyCentValue,
} from '@/utils/helpers';
import { showErrorToast, showSuccessToast } from '@/utils/toasts';
import type {
  JournalEntrySource,
  JournalOptionTrade,
  JournalStockPosition,
  JournalTag,
  JournalTagID,
} from '@/models';
import {
  ASSIGNMENT_DECISION_LABELS,
  JOURNAL_FIELD_CLASS,
  JOURNAL_LABEL_CLASS,
  OPTION_STATUS_LABELS,
  STOCK_STATUS_LABELS,
  STRATEGY_LABELS,
  optionStatusBadgeClass,
  pnlColorClass,
  readableTextColor,
} from '@/components/journal/journalHelpers';
import { useJournalResource } from '@/components/journal/useJournalResource';

const SOURCE_LABELS: Record<JournalEntrySource, string> = {
  MANUAL: 'Manuell erfasst',
  IB: 'Aus IB importiert',
};

const DIRECTION_LABELS = {
  SELL_TO_OPEN: 'Verkauft (Sell to open)',
  BUY_TO_OPEN: 'Gekauft (Buy to open)',
} as const;

const fieldClass = `${JOURNAL_FIELD_CLASS} w-full`;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

interface TradeDetailData {
  /** null means the id does not exist (or is not ours) - rendered as a 404, not as an error. */
  trade: JournalOptionTrade | null;
  stockPosition: JournalStockPosition | null;
  rolledFrom: JournalOptionTrade | null;
  rolledTo: JournalOptionTrade | null;
  tags: JournalTag[];
}

function isMissing(err: unknown): boolean {
  return err instanceof APIRequestError && (err.errorcode === 'ENTITY_NOT_FOUND' || err.errorcode === 'FORBIDDEN');
}

/** yyyy-MM-dd only, so the UTC-midnight parse cancels out and the result is a whole number of days. */
function daysBetween(fromDate: string, toDate: string): number {
  const from = Date.parse(`${fromDate}T00:00:00.000Z`);
  const to = Date.parse(`${toDate}T00:00:00.000Z`);
  return Math.round((to - from) / MS_PER_DAY);
}

function pluralDays(count: number): string {
  return count === 1 ? '1 Tag' : `${count} Tage`;
}

function expiryCountdown(expiry: string, today: string): string {
  const diff = daysBetween(today, expiry);
  if (diff > 0) return `noch ${pluralDays(diff)}`;
  if (diff === 0) return 'verfällt heute';
  return `vor ${diff === -1 ? '1 Tag' : `${-diff} Tagen`} verfallen`;
}

function signedCurrency(cents: number, currency: string): string {
  const formatted = formatCurrencyCentValue(Math.abs(cents), currency);
  if (cents > 0) return `+${formatted}`;
  if (cents < 0) return `-${formatted}`;
  return formatted;
}

function shares(trade: JournalOptionTrade): number {
  return trade.quantity * (trade.multiplier > 0 ? trade.multiplier : 100);
}

function contractLabel(trade: JournalOptionTrade): string {
  return `${trade.symbolTicker} ${formatRFCDate(trade.expiry)} ${trade.right} ${formatCurrencyCentValue(trade.strike, trade.currency)} ×${trade.quantity}`;
}

interface BreakdownRowProps {
  label: string;
  /** The arithmetic behind the amount, e.g. "3,00 $ × 2 × 100". */
  formula?: string;
  value: string;
  valueClassName?: string;
  emphasis?: boolean;
}

function BreakdownRow({ label, formula, value, valueClassName, emphasis = false }: BreakdownRowProps) {
  return (
    // Emphasis is a tint, not a border: the container's `divide-y` already rules every row.
    // The negative margin bleeds the tint into the Card's `p-4`.
    <div className={`flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2 ${emphasis ? '-mx-4 px-4 bg-gray-50 dark:bg-gray-700/30' : ''}`}>
      <div className="min-w-0">
        <div className={emphasis ? 'text-sm font-semibold text-gray-900 dark:text-white' : 'text-sm text-gray-600 dark:text-gray-300'}>{label}</div>
        {formula !== undefined && <div className="text-xs text-gray-500 dark:text-gray-400 break-words">{formula}</div>}
      </div>
      <div className={`text-sm tabular-nums ${emphasis ? 'font-semibold' : 'font-medium'} ${valueClassName ?? 'text-gray-900 dark:text-white'}`}>{value}</div>
    </div>
  );
}

interface TimelineItemProps {
  date: string;
  title: string;
  accent?: boolean;
  children?: ReactNode;
}

function TimelineItem({ date, title, accent = false, children }: TimelineItemProps) {
  return (
    // The connecting line is the li's own left border, so the last item simply drops it.
    <li className="relative ml-1 pl-5 pb-4 last:pb-0 border-l border-gray-200 dark:border-gray-700 last:border-transparent">
      <span className="absolute -left-[6px] top-1.5 w-[11px] h-[11px] rounded-full ring-4 ring-white dark:ring-gray-800 bg-blue-500" aria-hidden="true" />
      <div className="text-xs text-gray-500 dark:text-gray-400">{date}</div>
      <div className={`text-sm ${accent ? 'font-semibold text-gray-900 dark:text-white' : 'font-medium text-gray-800 dark:text-gray-100'}`}>{title}</div>
      {children !== undefined && <div className="mt-1 text-sm text-gray-600 dark:text-gray-300">{children}</div>}
    </li>
  );
}

export default function JournalTradeDetail() {
  const { tradeId } = useParams<{ tradeId: string }>();
  const navigate = useNavigate();
  const today = dateToRFCDateValue(new Date());

  const { data, loading, error, reload } = useJournalResource<TradeDetailData>(
    async () => {
      const empty: TradeDetailData = { trade: null, stockPosition: null, rolledFrom: null, rolledTo: null, tags: [] };
      if (tradeId === undefined) return empty;

      let trade: JournalOptionTrade;
      try {
        trade = await APIRoutes.getJournalOptionTrade(tradeId);
      } catch (err) {
        if (isMissing(err)) return empty;
        throw err;
      }

      // Every lookup below is decoration - a failing one must not take the trade down with it.
      const linkedTrade = async (id: string | null): Promise<JournalOptionTrade | null> => {
        if (id === null) return null;
        try {
          return await APIRoutes.getJournalOptionTrade(id);
        } catch (err) {
          console.error('Failed to load linked trade:', err);
          return null;
        }
      };
      const assignedPosition = async (): Promise<JournalStockPosition | null> => {
        if (trade.assignedStockID === null) return null;
        try {
          const resp = await APIRoutes.listJournalStockPositions({ assignedOnly: true, limit: 200 });
          return resp.stockPositions.find((p) => p.id === trade.assignedStockID || p.originOptionID === trade.id) ?? null;
        } catch (err) {
          console.error('Failed to load assigned stock position:', err);
          return null;
        }
      };
      const allTags = async (): Promise<JournalTag[]> => {
        try {
          return (await APIRoutes.listJournalTags()).tags;
        } catch (err) {
          console.error('Failed to load tags:', err);
          return [];
        }
      };

      const [stockPosition, rolledFrom, rolledTo, tags] = await Promise.all([
        assignedPosition(),
        linkedTrade(trade.rolledFromID),
        linkedTrade(trade.rolledToID),
        allTags(),
      ]);
      return { trade, stockPosition, rolledFrom, rolledTo, tags };
    },
    [tradeId],
    'Fehler beim Laden des Trades',
  );

  const trade = data?.trade ?? null;
  const rolledFrom = data?.rolledFrom ?? null;
  const rolledTo = data?.rolledTo ?? null;
  const stockPosition = data?.stockPosition ?? null;

  const [noteEditing, setNoteEditing] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [savingField, setSavingField] = useState<'note' | 'mood' | 'tags' | null>(null);
  const [pendingNav, setPendingNav] = useState<string | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closePrice, setClosePrice] = useState('');
  const [closePriceTouched, setClosePriceTouched] = useState(false);
  const [closeFee, setCloseFee] = useState('0');
  const [closeDate, setCloseDate] = useState(today);
  const [actionBusy, setActionBusy] = useState(false);

  const noteDirty = noteEditing && trade !== null && noteDraft !== trade.note;

  useEffect(() => {
    setNoteEditing(false);
    setNoteDraft('');
  }, [tradeId]);

  useEffect(() => {
    if (!noteDirty) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => { window.removeEventListener('beforeunload', handleBeforeUnload); };
  }, [noteDirty]);

  // Every in-page link runs through this so an unsaved note is never dropped silently.
  const guardNav = (e: MouseEvent<HTMLAnchorElement>, to: string) => {
    if (!noteDirty) return;
    e.preventDefault();
    setPendingNav(to);
  };

  const patchTrade = async (
    field: 'note' | 'mood' | 'tags',
    body: { note?: string; mood?: number; tagIDs?: JournalTagID[] },
    successMessage: string,
  ): Promise<boolean> => {
    if (trade === null) return false;
    setSavingField(field);
    try {
      await APIRoutes.updateJournalOptionTrade(trade.id, body);
      showSuccessToast(successMessage);
      if (field === 'note') setNoteEditing(false);
      reload();
      return true;
    } catch (err) {
      console.error('Failed to update trade:', err);
      showErrorToast('Änderung konnte nicht gespeichert werden');
      return false;
    } finally {
      setSavingField(null);
    }
  };

  const confirmDelete = async () => {
    if (trade === null) return;
    setActionBusy(true);
    try {
      await APIRoutes.deleteJournalOptionTrade(trade.id);
      showSuccessToast('Trade gelöscht');
      navigate(JOURNAL_ROUTES.TRADES);
    } catch (err) {
      console.error('Failed to delete trade:', err);
      showErrorToast('Trade konnte nicht gelöscht werden');
      setActionBusy(false);
    }
  };

  const confirmAssign = async () => {
    if (trade === null) return;
    setActionBusy(true);
    try {
      await APIRoutes.assignJournalOptionTrade(trade.id, {});
      setAssignOpen(false);
      showSuccessToast('Zuteilung gebucht');
      reload();
    } catch (err) {
      console.error('Failed to assign trade:', err);
      showErrorToast('Zuteilung konnte nicht gebucht werden');
    } finally {
      setActionBusy(false);
    }
  };

  const closePriceNum = closePrice.trim() === '' ? Number.NaN : Number(closePrice);
  const closeFeeNum = closeFee.trim() === '' ? 0 : Number(closeFee);
  const closePriceError = closePrice.trim() === ''
    ? 'Bitte einen Schließkurs angeben.'
    : Number.isNaN(closePriceNum)
      ? 'Bitte eine gültige Zahl eingeben.'
      : closePriceNum < 0
        ? 'Der Schließkurs darf nicht negativ sein.'
        : null;
  const closeFeeError = Number.isNaN(closeFeeNum)
    ? 'Bitte eine gültige Zahl eingeben.'
    : closeFeeNum < 0
      ? 'Die Gebühr darf nicht negativ sein.'
      : null;
  const closeDateError = closeDate === '' ? 'Bitte ein Schließdatum angeben.' : null;
  const closeValid = closePriceError === null && closeFeeError === null && closeDateError === null;

  // Mirrors the server's formula so the user sees the booked value before confirming.
  const previewPnL = trade === null || !closeValid
    ? null
    : trade.netCredit
      + (trade.direction === 'SELL_TO_OPEN' ? -1 : 1) * toCurrencyCentValue(closePriceNum) * shares(trade)
      - toCurrencyCentValue(closeFeeNum);

  const submitClose = async () => {
    if (trade === null || !closeValid) return;
    setActionBusy(true);
    try {
      await APIRoutes.updateJournalOptionTrade(trade.id, {
        status: 'CLOSED',
        closeDate,
        closePrice: toCurrencyCentValue(closePriceNum),
        closeFee: toCurrencyCentValue(closeFeeNum),
      });
      setCloseOpen(false);
      showSuccessToast('Trade geschlossen');
      reload();
    } catch (err) {
      console.error('Failed to close trade:', err);
      showErrorToast('Trade konnte nicht geschlossen werden');
    } finally {
      setActionBusy(false);
    }
  };

  const backLink = (
    <Link
      to={JOURNAL_ROUTES.TRADES}
      onClick={(e) => guardNav(e, JOURNAL_ROUTES.TRADES)}
      className="inline-flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
    >
      <FontAwesomeIcon icon={faArrowLeft} className="w-3.5 h-3.5" />
      Alle Trades
    </Link>
  );

  if (error !== null) {
    return (
      <div className="space-y-4">
        {backLink}
        <ErrorState message={error} onRetry={reload} />
      </div>
    );
  }

  if (data === null && loading) {
    return (
      <div className="py-16 text-center" role="status" aria-label="Trade wird geladen">
        <FontAwesomeIcon icon={faSpinner} className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (trade === null) {
    return (
      <Card>
        <EmptyState
          icon={faBook}
          title="Trade nicht gefunden"
          description="Dieser Trade existiert nicht (mehr) oder gehört zu einem anderen Konto. Vielleicht wurde er gelöscht."
          action={<Button to={JOURNAL_ROUTES.TRADES} icon={faArrowLeft} variant="secondary">Zurück zur Trade-Liste</Button>}
        />
      </Card>
    );
  }

  const isShort = trade.direction === 'SELL_TO_OPEN';
  const contractShares = shares(trade);
  const premiumGross = trade.openPrice * contractShares;
  const isOpen = trade.status === 'OPEN';
  const canAssign = isOpen && trade.right === 'PUT' && isShort;
  const boundCapital = trade.strike * contractShares;
  const holdingUntil = trade.closeDate ?? today;
  const holdingDays = daysBetween(trade.openDate, holdingUntil);
  const underlyingPrice = trade.symbol?.currentData?.price ?? null;
  const underlyingCurrency = trade.symbol?.currentData?.currency ?? trade.currency;
  const tags = data?.tags ?? [];
  const selectedTags = tags.filter((t) => trade.tagIDs.includes(t.id));

  const openCloseDialog = () => {
    setClosePrice('');
    setClosePriceTouched(false);
    setCloseFee('0');
    setCloseDate(today);
    setCloseOpen(true);
  };

  const toggleTag = (tagID: JournalTagID) => {
    const next = trade.tagIDs.includes(tagID) ? trade.tagIDs.filter((id) => id !== tagID) : [...trade.tagIDs, tagID];
    void patchTrade('tags', { tagIDs: next }, 'Tags gespeichert');
  };

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 space-y-2">
            {backLink}
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white break-words">{contractLabel(trade)}</h2>
            <div className="flex flex-wrap items-center gap-2">
              <Badge colorClass={optionStatusBadgeClass(trade.status)}>{OPTION_STATUS_LABELS[trade.status]}</Badge>
              <Badge color="blue">{STRATEGY_LABELS[trade.strategy]}</Badge>
              <Badge color={trade.source === 'IB' ? 'orange' : 'gray'}>{SOURCE_LABELS[trade.source]}</Badge>
              <Badge color="gray">{DIRECTION_LABELS[trade.direction]}</Badge>
            </div>
            {trade.symbol?.name !== null && trade.symbol?.name !== undefined && (
              <p className="text-sm text-gray-500 dark:text-gray-400">{trade.symbol.name}</p>
            )}
            {trade.source === 'IB' && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Kontrakt und Ausführungen stammen aus dem IB-Import
                {trade.ibAccountID === '' ? '' : ` (Konto ${trade.ibAccountID})`} und werden beim nächsten Import überschrieben.
                Notiz, Stimmung und Tags gehören dir.
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-2 sm:justify-end sm:shrink-0">
            <Button variant="secondary" icon={faEdit} disabled={actionBusy} onClick={() => setEditOpen(true)}>Bearbeiten</Button>
            {isOpen && (
              <Button variant="secondary" icon={faXmark} disabled={actionBusy} onClick={openCloseDialog}>Schließen</Button>
            )}
            {canAssign && (
              <Button variant="secondary" icon={faArrowRightFromBracket} disabled={actionBusy} onClick={() => setAssignOpen(true)}>Zuteilung buchen</Button>
            )}
            <Button variant="danger" icon={faTrash} disabled={actionBusy} onClick={() => setDeleteOpen(true)}>Löschen</Button>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Gewinn & Verlust" icon={faCalculator}>
          <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
            <BreakdownRow
              label={isShort ? 'Prämie erhalten' : 'Prämie gezahlt'}
              formula={`${formatCurrencyCentValue(trade.openPrice, trade.currency)} × ${trade.quantity} Kontrakt${trade.quantity === 1 ? '' : 'e'} × ${trade.multiplier}`}
              value={signedCurrency(isShort ? premiumGross : -premiumGross, trade.currency)}
            />
            <BreakdownRow
              label="Eröffnungsgebühr"
              value={signedCurrency(-trade.openFee, trade.currency)}
            />
            <BreakdownRow
              label="Netto-Credit"
              formula={isShort ? 'Prämie abzüglich Gebühr' : 'Einsatz inklusive Gebühr'}
              value={formatCurrencyCentValue(trade.netCredit, trade.currency)}
              emphasis
            />

            {(trade.status === 'CLOSED' || trade.status === 'ROLLED') && trade.closePrice !== null && (
              <>
                <BreakdownRow
                  label={isShort ? 'Rückkauf' : 'Verkauf'}
                  formula={`${formatCurrencyCentValue(trade.closePrice, trade.currency)} × ${trade.quantity} × ${trade.multiplier}${trade.closeDate === null ? '' : ` am ${formatRFCDate(trade.closeDate)}`}`}
                  value={signedCurrency((isShort ? -1 : 1) * trade.closePrice * contractShares, trade.currency)}
                />
                <BreakdownRow label="Schließgebühr" value={signedCurrency(-(trade.closeFee ?? 0), trade.currency)} />
              </>
            )}

            {trade.status === 'EXPIRED' && (
              <BreakdownRow
                label="Wertlos verfallen"
                formula={`Am ${formatRFCDate(trade.expiry)} ohne Rückkauf ausgelaufen`}
                value={formatCurrencyCentValue(0, trade.currency)}
              />
            )}

            {trade.status === 'ASSIGNED' && (
              <BreakdownRow
                label="Zugeteilt"
                formula="Die Option selbst geht auf null - der Aktien-G/V liegt auf der Aktienposition"
                value={formatCurrencyCentValue(0, trade.currency)}
              />
            )}

            {trade.realizedPnL !== null && (
              <BreakdownRow
                label="Realisierter G/V"
                value={formatCurrencyCentValue(trade.realizedPnL, trade.currency)}
                valueClassName={pnlColorClass(trade.realizedPnL)}
                emphasis
              />
            )}

            {isOpen && (
              <>
                <BreakdownRow
                  label="Aktueller Optionskurs"
                  formula={trade.markPrice === null
                    ? 'Keine Marktdaten verfügbar'
                    : `Rückkaufwert ${formatCurrencyCentValue(trade.markPrice * contractShares, trade.currency)}`}
                  value={trade.markPrice === null ? '—' : formatCurrencyCentValue(trade.markPrice, trade.currency)}
                />
                <BreakdownRow
                  label="Unrealisierter G/V"
                  formula="Netto-Credit abzüglich der aktuellen Kosten zum Schließen"
                  value={trade.unrealizedPnL === null ? '—' : formatCurrencyCentValue(trade.unrealizedPnL, trade.currency)}
                  valueClassName={pnlColorClass(trade.unrealizedPnL)}
                  emphasis
                />
                {canAssign && (
                  <BreakdownRow
                    label="Gebundenes Kapital"
                    formula={`${formatCurrencyCentValue(trade.strike, trade.currency)} × ${trade.quantity} × ${trade.multiplier}`}
                    value={formatCurrencyCentValue(boundCapital, trade.currency)}
                  />
                )}
              </>
            )}
          </div>
        </Card>

        <Card title="Lebenslauf" icon={faRoute}>
          <ol className="space-y-0">
            {rolledFrom !== null && (
              <TimelineItem date={formatRFCDate(rolledFrom.closeDate ?? rolledFrom.openDate)} title="Aus einem Roll entstanden">
                <Link
                  to={JOURNAL_ROUTES.TRADE_DETAIL(rolledFrom.id)}
                  onClick={(e) => guardNav(e, JOURNAL_ROUTES.TRADE_DETAIL(rolledFrom.id))}
                  className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer break-words"
                >
                  Vorgänger: {contractLabel(rolledFrom)}
                </Link>
              </TimelineItem>
            )}

            <TimelineItem date={formatRFCDate(trade.openDate)} title={isShort ? 'Eröffnet - verkauft' : 'Eröffnet - gekauft'}>
              {formatCurrencyCentValue(trade.openPrice, trade.currency)} pro Kontrakt, {trade.quantity} × {trade.multiplier} Stück
              {' · '}Netto-Credit {formatCurrencyCentValue(trade.netCredit, trade.currency)}
            </TimelineItem>

            {trade.status === 'OPEN' && (
              <TimelineItem date={formatRFCDate(trade.expiry)} title="Verfall" accent>
                Die Position läuft noch - {expiryCountdown(trade.expiry, today)}.
              </TimelineItem>
            )}

            {trade.status === 'CLOSED' && (
              <TimelineItem date={formatRFCDate(trade.closeDate ?? trade.expiry)} title="Geschlossen" accent>
                {trade.closePrice === null
                  ? 'Ohne Schließkurs gebucht.'
                  : `${isShort ? 'Zurückgekauft' : 'Verkauft'} zu ${formatCurrencyCentValue(trade.closePrice, trade.currency)} pro Kontrakt.`}
              </TimelineItem>
            )}

            {trade.status === 'EXPIRED' && (
              <TimelineItem date={formatRFCDate(trade.closeDate ?? trade.expiry)} title="Wertlos verfallen" accent>
                Der komplette Netto-Credit ist realisiert.
              </TimelineItem>
            )}

            {trade.status === 'ASSIGNED' && (
              <TimelineItem date={formatRFCDate(trade.closeDate ?? trade.expiry)} title="Zugeteilt" accent>
                <Link
                  to={JOURNAL_ROUTES.ASSIGNMENTS}
                  onClick={(e) => guardNav(e, JOURNAL_ROUTES.ASSIGNMENTS)}
                  className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
                >
                  Zur entstandenen Aktienposition
                </Link>
              </TimelineItem>
            )}

            {trade.status === 'ROLLED' && (
              <TimelineItem date={formatRFCDate(trade.closeDate ?? trade.expiry)} title="Gerollt" accent>
                {rolledTo === null ? 'Der Nachfolge-Trade ist nicht mehr verfügbar.' : (
                  <Link
                    to={JOURNAL_ROUTES.TRADE_DETAIL(rolledTo.id)}
                    onClick={(e) => guardNav(e, JOURNAL_ROUTES.TRADE_DETAIL(rolledTo.id))}
                    className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer break-words"
                  >
                    Nachfolger: {contractLabel(rolledTo)}
                  </Link>
                )}
              </TimelineItem>
            )}

            {trade.status !== 'ROLLED' && rolledTo !== null && (
              <TimelineItem date={formatRFCDate(rolledTo.openDate)} title="Weitergerollt">
                <Link
                  to={JOURNAL_ROUTES.TRADE_DETAIL(rolledTo.id)}
                  onClick={(e) => guardNav(e, JOURNAL_ROUTES.TRADE_DETAIL(rolledTo.id))}
                  className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer break-words"
                >
                  Nachfolger: {contractLabel(rolledTo)}
                </Link>
              </TimelineItem>
            )}
          </ol>

          {stockPosition !== null && (
            <div className="mt-2 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <FontAwesomeIcon icon={faMoneyBillWave} className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  Aktienposition {stockPosition.symbolTicker}
                </h3>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge color="gray">{STOCK_STATUS_LABELS[stockPosition.status]}</Badge>
                  <Badge color="blue">{ASSIGNMENT_DECISION_LABELS[stockPosition.assignmentDecision]}</Badge>
                </div>
              </div>
              <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-500 dark:text-gray-400">Stück</dt>
                  <dd className="text-gray-900 dark:text-white tabular-nums">{stockPosition.quantity}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-500 dark:text-gray-400">Einstand</dt>
                  <dd className="text-gray-900 dark:text-white tabular-nums">{formatCurrencyCentValue(stockPosition.costBasis, stockPosition.currency)}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-500 dark:text-gray-400">Aktueller Kurs</dt>
                  <dd className="text-gray-900 dark:text-white tabular-nums">
                    {stockPosition.currentPrice === null ? '—' : formatCurrencyCentValue(stockPosition.currentPrice, stockPosition.currency)}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-500 dark:text-gray-400">Unrealisiert</dt>
                  <dd className={`tabular-nums font-medium ${pnlColorClass(stockPosition.unrealizedPnL)}`}>
                    {stockPosition.unrealizedPnL === null ? '—' : formatCurrencyCentValue(stockPosition.unrealizedPnL, stockPosition.currency)}
                  </dd>
                </div>
              </dl>
              <Link
                to={JOURNAL_ROUTES.ASSIGNMENTS}
                onClick={(e) => guardNav(e, JOURNAL_ROUTES.ASSIGNMENTS)}
                className="mt-3 inline-flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
              >
                <FontAwesomeIcon icon={faArrowRightFromBracket} className="w-3.5 h-3.5" />
                Zuteilung verwalten
              </Link>
            </div>
          )}
        </Card>
      </div>

      <Card
        title="Journal"
        icon={faBook}
        actions={noteEditing ? undefined : (
          <Button variant="secondary" size="sm" icon={faPen} onClick={() => { setNoteDraft(trade.note); setNoteEditing(true); }}>
            {trade.note.trim() === '' ? 'Notiz schreiben' : 'Notiz bearbeiten'}
          </Button>
        )}
      >
        <div className="space-y-6">
          <div>
            {noteEditing ? (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className={JOURNAL_LABEL_CLASS}>Notiz</span>
                  {noteDirty && <Badge color="yellow">Nicht gespeichert</Badge>}
                </div>
                <MarkdownEditor
                  value={noteDraft}
                  onChange={(value) => setNoteDraft(value ?? '')}
                  placeholder="Was war der Gedanke hinter diesem Trade? Was würdest du wieder so machen?"
                  disabled={savingField === 'note'}
                  height={260}
                />
                <div className="flex flex-wrap justify-end gap-2">
                  <Button
                    variant="secondary"
                    disabled={savingField === 'note'}
                    onClick={() => { if (noteDirty) { setPendingNav(''); } else { setNoteEditing(false); } }}
                  >
                    Abbrechen
                  </Button>
                  <Button
                    icon={faSave}
                    loading={savingField === 'note'}
                    disabled={!noteDirty}
                    onClick={() => { void patchTrade('note', { note: noteDraft }, 'Notiz gespeichert'); }}
                  >
                    Notiz speichern
                  </Button>
                </div>
              </div>
            ) : trade.note.trim() === '' ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Noch keine Notiz. Halte fest, warum du diesen Trade eingegangen bist - beim nächsten Review liest du genau das.
              </p>
            ) : (
              <MarkdownRenderer>{trade.note}</MarkdownRenderer>
            )}
          </div>

          <div>
            <span className={JOURNAL_LABEL_CLASS}>Stimmung beim Eingehen</span>
            <div className="flex flex-wrap items-center gap-2">
              {[1, 2, 3, 4, 5].map((m) => (
                <button
                  key={m}
                  type="button"
                  aria-label={`Stimmung ${m} von 5`}
                  aria-pressed={trade.mood === m}
                  disabled={savingField === 'mood'}
                  onClick={() => { if (trade.mood !== m) void patchTrade('mood', { mood: m }, 'Stimmung gespeichert'); }}
                  className={`w-10 h-10 rounded-lg text-sm font-medium cursor-pointer border disabled:opacity-60 ${
                    trade.mood === m
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'
                  }`}
                >
                  {m}
                </button>
              ))}
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {trade.mood === null ? 'Noch nicht bewertet' : '1 = mulmig, 5 = sehr überzeugt'}
              </span>
            </div>
          </div>

          <div>
            <span className={JOURNAL_LABEL_CLASS}>
              Tags
              {tags.length > 0 && (
                <span className="ml-2 font-normal text-gray-500 dark:text-gray-400">
                  {selectedTags.length === 0
                    ? 'zum Zuordnen anklicken'
                    : `${selectedTags.length} von ${tags.length} zugeordnet`}
                </span>
              )}
            </span>
            {tags.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Du hast noch keine Tags angelegt.{' '}
                <Link
                  to={JOURNAL_ROUTES.TAGS}
                  onClick={(e) => guardNav(e, JOURNAL_ROUTES.TAGS)}
                  className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer inline-flex items-center gap-1"
                >
                  <FontAwesomeIcon icon={faTag} className="w-3.5 h-3.5" />
                  Tags verwalten
                </Link>
                {' '}- danach kannst du Fehler und Regeln hier zuordnen.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => {
                  const selected = trade.tagIDs.includes(tag.id);
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      aria-label={`Tag ${tag.name}`}
                      aria-pressed={selected}
                      disabled={savingField === 'tags'}
                      onClick={() => toggleTag(tag.id)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer border disabled:opacity-60 ${
                        selected
                          ? 'border-transparent'
                          : 'text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700'
                      }`}
                      style={selected ? { backgroundColor: tag.color ?? '#2563eb', color: readableTextColor(tag.color ?? '#2563eb') } : undefined}
                    >
                      {selected && <FontAwesomeIcon icon={faCheck} className="w-3 h-3 mr-1" />}
                      {tag.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </Card>

      <Card title="Kontext" icon={faCircleInfo}>
        <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div>
            <dt className="text-gray-500 dark:text-gray-400">Verfall</dt>
            <dd className="mt-0.5 font-medium text-gray-900 dark:text-white">{formatRFCDate(trade.expiry)}</dd>
            <dd className="text-xs text-gray-500 dark:text-gray-400">{expiryCountdown(trade.expiry, today)}</dd>
          </div>
          <div>
            <dt className="text-gray-500 dark:text-gray-400">{isOpen ? 'Offen seit' : 'Haltedauer'}</dt>
            <dd className="mt-0.5 font-medium text-gray-900 dark:text-white">{pluralDays(Math.max(holdingDays, 0))}</dd>
            <dd className="text-xs text-gray-500 dark:text-gray-400">
              seit {formatRFCDate(trade.openDate)}
              {trade.closeDate === null ? '' : ` bis ${formatRFCDate(trade.closeDate)}`}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500 dark:text-gray-400">Kurs {trade.symbolTicker}</dt>
            <dd className="mt-0.5 font-medium text-gray-900 dark:text-white">
              {underlyingPrice === null ? '—' : formatCurrency(underlyingPrice, underlyingCurrency)}
            </dd>
            <dd className="text-xs text-gray-500 dark:text-gray-400">
              {underlyingPrice === null
                ? 'Keine Kursdaten hinterlegt'
                : `Strike ${formatCurrencyCentValue(trade.strike, trade.currency)}`}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500 dark:text-gray-400">Kontraktgröße</dt>
            <dd className="mt-0.5 font-medium text-gray-900 dark:text-white">{contractShares} Stück</dd>
            <dd className="text-xs text-gray-500 dark:text-gray-400">{trade.quantity} × {trade.multiplier}</dd>
          </div>
        </dl>
      </Card>

      <OptionTradeModal
        isOpen={editOpen}
        editTrade={trade}
        onClose={() => setEditOpen(false)}
        onSaved={() => { reload(); }}
      />

      <Modal isOpen={deleteOpen} onClose={() => setDeleteOpen(false)} title="Trade löschen" maxWidth="md" closeDisabled={actionBusy}>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Der Trade <span className="font-medium text-gray-900 dark:text-white">{contractLabel(trade)}</span> wird endgültig gelöscht - inklusive Notiz, Stimmung und Tags.
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-300">Das kann nicht rückgängig gemacht werden.</p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" disabled={actionBusy} onClick={() => setDeleteOpen(false)}>Abbrechen</Button>
            <Button variant="danger" loading={actionBusy} onClick={confirmDelete}>Löschen</Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={assignOpen} onClose={() => setAssignOpen(false)} title="Zuteilung buchen" maxWidth="md" closeDisabled={actionBusy}>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Für <span className="font-medium text-gray-900 dark:text-white">{contractLabel(trade)}</span> wird die Zuteilung gebucht.
          </p>
          <ul className="text-sm text-gray-600 dark:text-gray-300 list-disc pl-5 space-y-1">
            <li>
              Es entsteht eine Aktienposition über <span className="font-medium text-gray-900 dark:text-white">{contractShares} Aktien</span> zum Strike von {formatCurrencyCentValue(trade.strike, trade.currency)} (Einstandswert {formatCurrencyCentValue(boundCapital, trade.currency)}).
            </li>
            <li>Der Options-Trade wird mit dem Status „Zugeteilt“ geschlossen.</li>
          </ul>
          <p className="text-sm font-medium text-red-600 dark:text-red-400">Diese Buchung kann nicht rückgängig gemacht werden.</p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" disabled={actionBusy} onClick={() => setAssignOpen(false)}>Abbrechen</Button>
            <Button variant="danger" loading={actionBusy} onClick={confirmAssign}>Zuteilung buchen</Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={closeOpen} onClose={() => setCloseOpen(false)} title="Trade schließen" maxWidth="md" closeDisabled={actionBusy}>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {`${trade.symbolTicker} ${trade.right} ${formatCurrencyCentValue(trade.strike, trade.currency)} ${isShort ? 'zurückkaufen' : 'verkaufen'}.`}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={JOURNAL_LABEL_CLASS} htmlFor="detail-close-price">Schließkurs / Kontrakt ($)</label>
              <input
                id="detail-close-price"
                className={`${fieldClass} ${!closePriceTouched || closePriceError === null ? '' : 'border-red-500 dark:border-red-500'}`}
                type="number"
                step="0.01"
                value={closePrice}
                onChange={(e) => { setClosePrice(e.target.value); setClosePriceTouched(true); }}
                onBlur={() => setClosePriceTouched(true)}
                placeholder="0.50"
                aria-invalid={closePriceError !== null}
              />
              {closePriceTouched && closePriceError !== null && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{closePriceError}</p>}
            </div>
            <div>
              <label className={JOURNAL_LABEL_CLASS} htmlFor="detail-close-fee">Gebühr ($)</label>
              <input
                id="detail-close-fee"
                className={`${fieldClass} ${closeFeeError === null ? '' : 'border-red-500 dark:border-red-500'}`}
                type="number"
                step="0.01"
                value={closeFee}
                onChange={(e) => setCloseFee(e.target.value)}
                aria-invalid={closeFeeError !== null}
              />
              {closeFeeError !== null && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{closeFeeError}</p>}
            </div>
            <div>
              <label className={JOURNAL_LABEL_CLASS} htmlFor="detail-close-date">Schließdatum</label>
              <input
                id="detail-close-date"
                className={`${fieldClass} ${closeDateError === null ? '' : 'border-red-500 dark:border-red-500'}`}
                type="date"
                value={closeDate}
                onChange={(e) => setCloseDate(e.target.value)}
                aria-invalid={closeDateError !== null}
              />
              {closeDateError !== null && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{closeDateError}</p>}
            </div>
          </div>
          {previewPnL !== null && (
            <div className="flex items-baseline justify-between gap-3 rounded-lg bg-gray-50 dark:bg-gray-700/40 px-3 py-2">
              <span className="text-sm text-gray-600 dark:text-gray-300">Voraussichtlich realisiert</span>
              <span className={`text-sm font-semibold ${pnlColorClass(previewPnL)}`}>{formatCurrencyCentValue(previewPnL, trade.currency)}</span>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" disabled={actionBusy} onClick={() => setCloseOpen(false)}>Abbrechen</Button>
            <Button variant="primary" loading={actionBusy} disabled={!closeValid} onClick={submitClose}>Schließen</Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={pendingNav !== null} onClose={() => setPendingNav(null)} title="Notiz nicht gespeichert" maxWidth="md">
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Deine Notiz zu <span className="font-medium text-gray-900 dark:text-white">{contractLabel(trade)}</span> ist noch nicht gespeichert.
            Wenn du fortfährst, geht der Text verloren.
          </p>
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="secondary" disabled={savingField === 'note'} onClick={() => setPendingNav(null)}>Weiter bearbeiten</Button>
            <Button
              icon={faSave}
              loading={savingField === 'note'}
              onClick={async () => {
                const target = pendingNav;
                if (!await patchTrade('note', { note: noteDraft }, 'Notiz gespeichert')) return;
                setPendingNav(null);
                if (target !== null && target !== '') navigate(target);
              }}
            >
              Speichern
            </Button>
            <Button
              variant="danger"
              disabled={savingField === 'note'}
              onClick={() => {
                const target = pendingNav;
                setNoteEditing(false);
                setNoteDraft('');
                setPendingNav(null);
                if (target !== null && target !== '') navigate(target);
              }}
            >
              Verwerfen
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
